import { useState } from 'react';
import type { ClientCard, ContactRole, RepKind } from '@/api/base-types';
import { useBaseMutation } from '@/api/base';
import { api } from '@/api/client';
import { ChoiceField } from '@/components/kit/Controls';
import { DateInput } from '@/components/kit/DateInput';
import { PhoneField, TextField } from '@/components/kit/Field';
import { Modal } from '@/components/kit/Modal';
import { Note } from '@/components/kit/Note';
import { OptionList } from '@/components/kit/OptionList';
import { useToast } from '@/components/kit/Toast';
import { formatPhone, years } from '@/lib/phone';
import { CityField, type CityValue } from '../shared/CityField';
import { ClientPicker, emptyPicker, pickerPayload, pickerReady, type PickerState } from '../shared/ClientPicker';
import { StudioPicker, TeamPicker } from '../shared/EntityPickers';
import { CONTACT_ROLE_ITEMS, REP_KIND, REP_KIND_ITEMS } from '../shared/labels';
import { errorText } from '../shared/useUndoable';

/** Общая обвязка: мутация, тост, закрытие; ошибка — текстом сервера. */
function useSubmit<T>(fn: () => Promise<T>, done: string, onClose: () => void) {
  const toast = useToast();
  const m = useBaseMutation(fn);
  return {
    pending: m.isPending,
    submit: () =>
      m.mutate(undefined, {
        onSuccess: () => {
          toast.show(done);
          onClose();
        },
        onError: (e) => toast.showError(errorText(e)),
      }),
  };
}

/** Изменить данные клиента (fEdit). Ответственный — пилюлей в шапке, не здесь. */
export function EditClientModal({ client, onClose }: { client: ClientCard; onClose: () => void }) {
  const [lastName, setLastName] = useState(client.lastName ?? '');
  const [firstName, setFirstName] = useState(client.firstName ?? '');
  const [middleName, setMiddleName] = useState(client.middleName ?? '');
  const [birthDate, setBirthDate] = useState<string | null>(client.birthDate);
  const [birthValid, setBirthValid] = useState(true);
  const [phone, setPhone] = useState(formatPhone(client.phone));
  const [email, setEmail] = useState(client.email ?? '');
  const [city, setCity] = useState<CityValue | null>(client.city);
  const [telegram, setTelegram] = useState(client.telegramUsername ?? '');
  const [instagram, setInstagram] = useState(client.instagram ?? '');

  const { submit, pending } = useSubmit(
    () =>
      api.patch(`/clients/${client.id}`, {
        lastName,
        firstName,
        middleName: middleName || null,
        birthDate,
        phone: phone.trim() || null,
        email: email.trim() || null,
        cityId: city?.id ?? null,
        telegramUsername: telegram.trim() || null,
        instagram: instagram.trim() || null,
      }),
    'Данные сохранены',
    onClose,
  );

  return (
    <Modal
      title="Изменить данные клиента"
      onClose={onClose}
      confirm={{
        label: 'Сохранить',
        onClick: submit,
        disabled: !lastName.trim() || !firstName.trim() || !birthValid || pending,
      }}
    >
      <div className="row keep">
        <div className="col">
          <TextField label="Фамилия" value={lastName} onValue={setLastName} />
        </div>
        <div className="col">
          <TextField label="Имя" value={firstName} onValue={setFirstName} />
        </div>
      </div>
      <TextField label="Отчество" value={middleName} onValue={setMiddleName} />
      <div className="row keep">
        <div className="col">
          <DateInput
            label="Дата рождения"
            value={birthDate}
            onChange={(v, ok) => {
              setBirthDate(v);
              setBirthValid(ok);
            }}
          />
        </div>
        <div className="col">
          <PhoneField label="Телефон" value={phone} onValue={setPhone} />
        </div>
      </div>
      <div className="row keep">
        <div className="col">
          <TextField label="Почта" inputMode="email" value={email} onValue={setEmail} />
        </div>
        <div className="col">
          <CityField inModal value={city} onChange={setCity} />
        </div>
      </div>
      {/* Ник, «@ник» или ссылка — сервер приведёт к нику (доработка M2-1 п. 1) */}
      <div className="row keep">
        <div className="col">
          <TextField
            label="Telegram"
            placeholder="ник без @, пока бот не подключён"
            value={telegram}
            onValue={setTelegram}
          />
        </div>
        <div className="col">
          <TextField label="Instagram" placeholder="ник без @" value={instagram} onValue={setInstagram} />
        </div>
      </div>
    </Modal>
  );
}

/** Включить в команду с карточки клиента (fToTeam). */
export function ToTeamModal({ client, onClose }: { client: ClientCard; onClose: () => void }) {
  const [teamId, setTeamId] = useState<string | null>(null);
  const [birthDate, setBirthDate] = useState<string | null>(null);
  const [birthValid, setBirthValid] = useState(true);
  const activeTeams = client.teams.filter((t) => t.membership && !t.membership.leftOn).map((t) => t.team.id);
  const { submit, pending } = useSubmit(
    () => api.post(`/clients/${client.id}/teams`, { teamId, ...(client.birthDate ? {} : { birthDate }) }),
    'Включён(а) в команду',
    onClose,
  );
  return (
    <Modal
      title="Включить в команду"
      onClose={onClose}
      confirm={{
        label: 'Включить',
        onClick: submit,
        disabled: !teamId || (!client.birthDate && (!birthDate || !birthValid)) || pending,
      }}
    >
      <p>
        <b>{client.fullName}</b> войдёт в состав с сегодняшнего дня.
      </p>
      <TeamPicker value={teamId} onChange={setTeamId} exclude={activeTeams} />
      {!client.birthDate ? (
        <DateInput
          label="Дата рождения — нужна участнику"
          value={birthDate}
          onChange={(v, ok) => {
            setBirthDate(v);
            setBirthValid(ok);
          }}
        />
      ) : null}
    </Modal>
  );
}

/** Добавить представителя (fAddRep): с телефоном, кем приходится. */
export function AddRepresentativeModal({ client, onClose }: { client: ClientCard; onClose: () => void }) {
  const [rep, setRep] = useState<PickerState>(emptyPicker());
  const [kind, setKind] = useState<RepKind>('mother');
  const { submit, pending } = useSubmit(
    () => api.post(`/clients/${client.id}/representatives`, { rep: pickerPayload(rep, 'phone'), kind }),
    'Представитель добавлен',
    onClose,
  );
  return (
    <Modal
      title="Добавить представителя"
      size="wide"
      onClose={onClose}
      confirm={{ label: 'Добавить', onClick: submit, disabled: !pickerReady(rep, 'phone') || pending }}
    >
      <p>
        Родитель или законный представитель <b>{client.fullName}</b>: с ним держим связь, он может принимать документы
        за ребёнка.
      </p>
      <ClientPicker
        label="Представитель"
        need="phone"
        value={rep}
        onChange={setRep}
        exclude={[client.id, ...client.representatives.map((r) => r.client.id)]}
      />
      <ChoiceField label="Кем приходится" items={REP_KIND_ITEMS} value={kind} onChange={setKind} />
    </Modal>
  );
}

/** Добавить ребёнка с карточки взрослого (fAddWard): ребёнок с датой рождения. */
export function AddWardModal({ client, onClose }: { client: ClientCard; onClose: () => void }) {
  const [child, setChild] = useState<PickerState>(emptyPicker());
  const [kind, setKind] = useState<RepKind>('mother');
  const [phone, setPhone] = useState('');
  const { submit, pending } = useSubmit(
    () =>
      api.post(`/clients/${client.id}/wards`, {
        child: pickerPayload(child, 'birth'),
        kind,
        ...(client.phone ? {} : { phone }),
      }),
    'Ребёнок добавлен',
    onClose,
  );
  return (
    <Modal
      title="Добавить ребёнка"
      size="wide"
      onClose={onClose}
      confirm={{
        label: 'Добавить',
        onClick: submit,
        disabled: !pickerReady(child, 'birth') || (!client.phone && !phone.trim()) || pending,
      }}
    >
      <p>
        <b>{client.fullName}</b> станет представителем ребёнка.
      </p>
      {!client.phone ? (
        <PhoneField
          label="Телефон представителя — нужен для роли"
          value={phone}
          onValue={setPhone}
        />
      ) : null}
      <ClientPicker
        label="Ребёнок"
        need="birth"
        value={child}
        onChange={setChild}
        exclude={[client.id, ...client.wards.map((w) => w.client.id)]}
      />
      <ChoiceField label="Кем приходится ребёнку" items={REP_KIND_ITEMS} value={kind} onChange={setKind} />
    </Modal>
  );
}

/** Привязать к студии с карточки клиента (fToStudio): студия, роль, телефон при отсутствии. */
export function ToStudioModal({ client, onClose }: { client: ClientCard; onClose: () => void }) {
  const [studio, setStudio] = useState<{ id: string; name: string } | null>(null);
  const [role, setRole] = useState<ContactRole>('head');
  const [phone, setPhone] = useState('');
  const { submit, pending } = useSubmit(
    () => api.post(`/clients/${client.id}/studios`, { studioId: studio?.id, role, ...(client.phone ? {} : { phone }) }),
    'Клиент привязан к студии',
    onClose,
  );
  return (
    <Modal
      title="Привязать к студии"
      onClose={onClose}
      confirm={{
        label: 'Привязать',
        onClick: submit,
        disabled: !studio || (!client.phone && !phone.trim()) || pending,
      }}
    >
      <p>
        <b>{client.fullName}</b> станет контактом студии.
      </p>
      <StudioPicker value={studio} onChange={setStudio} />
      <ChoiceField label="Роль в студии" items={CONTACT_ROLE_ITEMS} value={role} onChange={setRole} />
      {!client.phone ? (
        <PhoneField
          label="Телефон — нужен контакту студии"
          value={phone}
          onValue={setPhone}
        />
      ) : null}
    </Modal>
  );
}

const todayIso = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

/**
 * Отметить принятие документов вручную (БЛ 3.5): запасной путь, пока
 * нет сайта. Кто принял — сам совершеннолетний или представитель.
 */
export function AcceptanceModal({ client, onClose }: { client: ClientCard; onClose: () => void }) {
  const adult = client.age !== null && client.age >= 18;
  const options = adult
    ? [{ value: client.id, title: 'Сам участник', description: `${client.fullName}${client.age !== null ? ` · ${years(client.age)}` : ''}` }]
    : client.representatives.map((r) => ({
        value: r.client.id,
        title: r.client.fullName,
        description: `${REP_KIND[r.kind]} · ${formatPhone(r.client.phone)}`,
      }));
  const [by, setBy] = useState<string | null>(options[0]?.value ?? null);
  const [date, setDate] = useState<string | null>(todayIso());
  const [dateValid, setDateValid] = useState(true);
  const { submit, pending } = useSubmit(
    () => api.post(`/clients/${client.id}/acceptances`, { acceptedByClientId: by, acceptedOn: date }),
    'Принятие документов отмечено',
    onClose,
  );

  return (
    <Modal
      title="Отметить принятие документов"
      onClose={onClose}
      confirm={{ label: 'Отметить', onClick: submit, disabled: !by || !date || !dateValid || pending }}
    >
      <p>
        Обычно документы принимают галочкой на сайте — при регистрации или покупке, и система отмечает это сама.
        Вручную — только если участник принял их иначе, например подписал на бумаге.
      </p>
      <p>
        <b>{client.fullName}</b> · оферта, политика и согласие на обработку и публикацию данных.
      </p>
      {options.length ? (
        <>
          <p className="sec">Кто принял</p>
          <OptionList value={by} onChange={setBy} options={options} />
          <DateInput
            label="Дата"
            value={date}
            onChange={(v, ok) => {
              setDate(v);
              setDateValid(ok);
            }}
          />
        </>
      ) : (
        <div className="mnote">
          <Note tone="warn">
            <b>Сначала нужен представитель.</b> За несовершеннолетнего документы принимает родитель или законный
            представитель.
          </Note>
        </div>
      )}
    </Modal>
  );
}
